// src/pages/historical-research-archive/components/CoachItem.jsx
import React, { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../../../components/ui/Header";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import Button from "../../../components/ui/Button";
import coaches from "../../../data/coaches";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import Footer from "../../../components/ui/Footer";
import WhatsAppButton from "../../../components/ui/WhatsAppButton";

const formatDate = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (isNaN(d)) return dateString;
    return format(d, "dd 'de' MMMM yyyy", { locale: es });
};

export default function CoachItem() {
    const { id } = useParams();
    const coachId = parseInt(id, 10);

    // buscar coach por id en el array importado
    const coach = useMemo(() => coaches.find((c) => c.id === coachId), [coachId]);
    const WHATSAPP_NUMBER = "51999202119";

    return (
        <div className="min-h-screen bg-background">
            <Header />

            <main className="pt-16 lg:pt-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    {!coach ? (
                        <div className="bg-white rounded-lg literary-shadow p-8 text-center">
                            <h2 className="font-headline text-2xl font-bold text-primary mb-2">
                                Taller no encontrado
                            </h2>
                            <p className="font-body text-scholarly-gray mb-6">
                                No se encontró el taller solicitado. Puedes volver a la lista de talleres.
                            </p>
                            <Link to="/historical-research-archive">
                                <Button variant="outline" size="md" className="font-cta">
                                    Volver a Talleres
                                </Button>
                            </Link>
                        </div>
                    ) : (
                        <article className="bg-white rounded-2xl literary-border literary-shadow overflow-hidden">
                            {/* Imagen principal */}
                            <div className="w-full h-56 sm:h-72 md:h-96 overflow-hidden">
                                <Image
                                    src={coach.image}
                                    alt={coach.title}
                                    className="w-full h-full object-cover"
                                />
                            </div>

                            <div className="p-8 space-y-6">
                                {/* Título */}
                                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                                    <div className="max-w-full md:max-w-[70%]">
                                        <h1
                                            className="
                                                font-headline
                                                text-lg
                                                sm:text-xl
                                                lg:text-3xl
                                                font-bold
                                                text-primary
                                                mb-3
                                                leading-snug
                                                sm:leading-tight
                                                tracking-tight
                                                text-balance
                                              "
                                        >
                                            {coach.title}
                                        </h1>
                                        <p className="text-sm text-scholarly-gray">
                                            <strong>Organiza:</strong> {coach.organizer}
                                        </p>
                                    </div>

                                    {/* CTA lateral (desktop) */}
                                    <div className="hidden md:flex md:flex-col md:items-end md:space-y-3">
                                        <div className="text-right">
                                            <div className="text-sm text-scholarly-gray mb-1">
                                                <Icon name="Calendar" size={14} className="inline-block mr-2 text-blue-600" />
                                                <span className="font-body">
                                                  {formatDate(coach.date?.beginning)}{" "}
                                                    {coach.date?.end ? ` - ${formatDate(coach.date.end)}` : ""}
                                                </span>
                                            </div>
                                            <div className="text-sm text-scholarly-gray mb-1">
                                                <Icon name="Clock" size={14} className="inline-block mr-2 text-amber-600" />
                                                <span className="font-body">{coach.hour}</span>
                                            </div>
                                            <div className="text-sm text-scholarly-gray">
                                                <Icon name="MapPin" size={14} className="inline-block mr-2 text-red-600" />
                                                <span className="font-body">{coach.location?.name}</span>
                                            </div>
                                        </div>

                                        <Button size="lg" variant="default" className="bg-burnished-copper text-white font-cta" asChild>
                                            <a
                                                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label="Inscribirme por WhatsApp"
                                                className="flex items-center justify-center gap-2"
                                            >
                                                <Icon name="MessageCircle" size={16} />
                                                Inscribirme
                                            </a>
                                        </Button>
                                    </div>
                                </div>

                                {/* Meta (móvil) */}
                                <div className="md:hidden grid grid-cols-1 gap-2 text-sm text-scholarly-gray">
                                    <div className="flex items-center space-x-3">
                                        <Icon name="Calendar" size={14} className="text-blue-600" />
                                        <span>{formatDate(coach.date?.beginning)}{coach.date?.end ? ` - ${formatDate(coach.date.end)}` : ""}</span>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <Icon name="Clock" size={14} className="text-amber-600" />
                                        <span>{coach.hour}</span>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <Icon name="MapPin" size={14} className="text-red-600" />
                                        <span>{coach.location?.name}</span>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <Icon name="Laptop" size={14} className="text-green-600" />
                                        <span>{(coach.mode || []).join(", ")}</span>
                                    </div>

                                    <div className="pt-3">
                                        <Button size="lg" variant="default" className="bg-burnished-copper text-white font-cta w-full" asChild>
                                            <a
                                                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label="Inscribirme por WhatsApp"
                                                className="flex items-center justify-center gap-2"
                                            >
                                                <Icon name="MessageCircle" size={16} />
                                                Inscribirme
                                            </a>
                                        </Button>
                                    </div>
                                </div>

                                {/* Descripción */}
                                <div>
                                    <h3 className="font-headline text-lg font-semibold text-primary mb-2">
                                        Descripción
                                    </h3>
                                    <p className="font-body text-scholarly-gray whitespace-pre-line text-justify">
                                        {coach.description}
                                    </p>
                                </div>


                                {/* Temario */}
                                {coach.themes?.length > 0 && (
                                    <div>
                                        <h3 className="font-headline text-lg font-semibold text-primary mb-2">
                                            Temario
                                        </h3>
                                        <ul className="list-disc list-outside text-scholarly-gray space-y-1">
                                            {coach.themes.map((t, i) => (
                                                <li key={i} className="text-sm text-justify">
                                                    {t}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Requisitos / notas / contacto */}
                                <div className="grid md:grid-cols-2 gap-6">
                                    {coach.requirements?.length > 0 && (
                                        <div>
                                            <h4 className="font-headline text-md font-semibold text-primary mb-2">Requisitos</h4>
                                            <ul className="list-disc list-inside text-scholarly-gray text-sm">
                                                {coach.requirements.map((r, i) => <li key={i}>{r}</li>)}
                                            </ul>
                                        </div>
                                    )}

                                    <div>
                                        <h4 className="font-headline text-md font-semibold text-primary mb-2">Contacto</h4>
                                        <p className="text-sm text-scholarly-gray">
                                            WhatsApp:{" "}
                                            <a className="text-blue-600 hover:underline" href={`https://wa.me/${WHATSAPP_NUMBER}`}
                                               target="_blank" rel="noopener noreferrer">
                                                {coach.contact?.whatsapp}
                                            </a>
                                        </p>
                                    </div>
                                </div>

                                {/* Notas finales */}
                                {coach.notes && <p className="text-sm text-gray-600 italic pt-4 border-t border-gray-100">{coach.notes}</p>}

                                {/* Back */}
                                <div className="mt-6">
                                    <Link to="/historical-research-archive">
                                        <Button variant="ghost" size="sm" className="font-cta">
                                            ← Volver a Talleres
                                        </Button>
                                    </Link>
                                </div>
                            </div>
                        </article>
                    )}
                </div>
                <WhatsAppButton />
            </main>
            <Footer/>
        </div>
    );
}
